import React from 'react';
import { Button } from 'reactstrap';
import { AccentButton } from '../AccentButton';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { accentUtils, t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/**
 * Prompt composer (text area + send action(s)).
 *
 * Props:
 * - value
 * - onChange(value)
 * - onSend({ text, intent }): async allowed; called via AccentButton promise pattern.
 *   intent is 'ask' | 'change' when the composer offers the intent toggle, and
 *   undefined on legacy single-button hosts — the AI then infers intent.
 * - askLabel?: when set, a MODE TOGGLE renders — an "ask" segment with this label and
 *   a "change" segment labeled by sendLabel — plus a single Send button. The toggle
 *   selects the intent ('change' by default, sticky across sends); both the Send
 *   button and Enter send with the SELECTED intent. When unset, the single legacy
 *   Send button renders and no intent is sent.
 * - sendLabel?: label of the 'change' toggle segment (and of the single legacy Send
 *   button when askLabel is unset; defaults to 'Send')
 * - disabled?
 * - placeholder?
 *
 * Ref (imperative):
 * - send(text, intent): sends the given text as if the user clicked Send — same
 *   onSend flow, same button spinner/self-disable. No-ops when disabled or empty.
 */
export const AccentPromptComposer = React.forwardRef(({
  value,
  onChange,
  onSend,
  disabled,
  label,
  placeholder,
  templateCatalog,
  askLabel,
  sendLabel,
  rows = 3,
  max = 4000,
}, ref) => {
  const text = safeString(value);
  const canSend = !disabled && !accentUtils.isEmpty(text);
  const hasIntentToggle = !accentUtils.isEmpty(safeString(askLabel));

  // Sticky on purpose: a user asking several questions in a row should not have to
  // re-select the mode before every message. 'change' is the default because most
  // prompts are change requests, and 'ask' turns are server-enforced read-only anyway.
  const [intentMode, setIntentMode] = React.useState('change');

  const sendBtnRef = React.useRef(null);
  // Set just before a programmatic click so the button's onClick sends the supplied
  // text/intent instead of the textarea content. A ref (not state) so the pending
  // send is visible to the click handler in the same tick, without a re-render.
  const pendingSendRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    send: (sendText, intent) => {
      pendingSendRef.current = { text: safeString(sendText), intent };
      sendBtnRef.current?.click();
    },
  }));

  const sendWithIntent = async (e, sendText, intent) => {
    try {
      await onSend?.({ text: sendText, intent });
    } finally {
      if (e?.promise) e.promise();
    }
  };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <AccentTemplateTextAreaUnbound
        label={label ?? t('Prompt')}
        rows={rows}
        max={max}
        disabled={!!disabled}
        placeholder={placeholder}
        templateCatalog={templateCatalog}
        insertMode="snippet"
        defaultValue={text}
        onChange={onChange}
        onKeyDown={(e) => {
          const key = e?.key ?? e?.nativeEvent?.key;
          const isEnter = key === 'Enter';
          // e.repeat guards a held-down Enter: auto-repeat would queue a second click
          // before the first send disables the button.
          const isRepeat = !!(e?.repeat ?? e?.nativeEvent?.repeat);
          const allowSubmit = isEnter && !isRepeat && !e?.shiftKey && !e?.altKey && !e?.ctrlKey && !e?.metaKey && !e?.isComposing;
          if (!allowSubmit) return;

          if (e?.preventDefault) e.preventDefault();
          if (!canSend) return;

          // Enter routes through the Send button (not onSend directly) so it gets the
          // identical click flow: self-disable, the double-send debounce and the
          // built-in spinner for the whole round-trip. The button's onClick reads the
          // textarea text and the SELECTED mode on intent-toggle hosts (legacy
          // single-button hosts keep sending without an intent — the AI infers).
          sendBtnRef.current?.click();
        }}
        style={{ width: '100%' }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
        {hasIntentToggle && (
          // Plain reactstrap buttons: AccentButton's click debounce/self-disable suits
          // actions, not a mode switch that must flip instantly.
          <div className="btn-group" role="group" data-tagname="promptIntentToggle">
            <Button
              size="sm"
              type="button"
              color="primary"
              outline={intentMode !== 'ask'}
              active={intentMode === 'ask'}
              disabled={!!disabled}
              onClick={() => setIntentMode('ask')}
            >
              {askLabel}
            </Button>
            <Button
              size="sm"
              type="button"
              color="primary"
              outline={intentMode !== 'change'}
              active={intentMode === 'change'}
              disabled={!!disabled}
              onClick={() => setIntentMode('change')}
            >
              {safeString(sendLabel) || t('Send')}
            </Button>
          </div>
        )}
        {/* No disabled prop on purpose: a controlled disabled prop makes AccentButton
            drop its internal click state on any parent re-render, which kills the
            built-in spinner mid-send. The guard below no-ops instead. */}
        <AccentButton
          ref={sendBtnRef}
          tagName="promptSend"
          onClick={async (e) => {
            const pending = pendingSendRef.current;
            pendingSendRef.current = null;
            const sendText = pending ? pending.text : text;
            const sendIntent = pending ? pending.intent : (hasIntentToggle ? intentMode : undefined);
            if (disabled || accentUtils.isEmpty(sendText)) {
              e.promise();
              return;
            }
            await sendWithIntent(e, sendText, sendIntent);
          }}
        >
          {hasIntentToggle ? t('Send') : (safeString(sendLabel) || t('Send'))}
        </AccentButton>
      </div>
    </div>
  );
});
